import React, { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Copy, Trash2, Loader2, Images } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Skeleton } from "../../components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { validateImageFile } from "../../components/admin/ImageField";
import { errorMessage, resolveMedia, uploadImage, uploadsApi } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/avif";
const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);
const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const MediaCard = ({ item, onDelete }) => (
  <div className="group flex flex-col border border-border bg-card" data-testid={`media-item-${item.id}`}>
    <div className="relative aspect-[4/3] overflow-hidden bg-background">
      <img src={resolveMedia(item.url)} alt={item.original_name} loading="lazy" className="h-full w-full object-cover" />
    </div>
    <div className="flex flex-1 flex-col p-3">
      <p className="truncate text-sm font-semibold" title={item.original_name}>{item.original_name}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {fmtSize(item.size)} · {fmtDate(item.created_at)}
      </p>
      <div className="mt-3 flex items-center gap-1">
        <Button size="sm" variant="outline" className="h-8 flex-1 rounded-none text-[10px] uppercase tracking-[0.16em]" onClick={() => copyToClipboard(resolveMedia(item.url), "Image link copied")} data-testid={`media-copy-${item.id}`}>
          <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy link
        </Button>
        <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-destructive" onClick={() => onDelete(item)} aria-label="Delete image" data-testid={`media-delete-${item.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  </div>
);

const CRUMBS = [{ label: "Media Library" }];

const MediaLibrary = () => {
  const inputRef = useRef(null);
  const [items, setItems] = useState(null);
  const [progress, setProgress] = useState(null);
  const [fileName, setFileName] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      setItems(await uploadsApi.list());
    } catch (err) {
      toast.error(errorMessage(err, "Could not load media"));
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    for (const file of list) {
      const problem = validateImageFile(file);
      if (problem) {
        toast.error(`${file.name}: ${problem}`);
        continue;
      }
      setFileName(file.name);
      setProgress(0);
      try {
        // eslint-disable-next-line no-await-in-loop -- uploads run one after another on purpose
        const rec = await uploadImage(file, setProgress);
        setItems((prev) => [rec, ...(prev || [])]);
        toast.success("Image uploaded", { description: file.name });
      } catch (err) {
        toast.error(errorMessage(err, `Upload failed: ${file.name}`));
      }
    }
    setTimeout(() => setProgress(null), 600);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await uploadsApi.remove(toDelete.id);
      setItems((prev) => prev.filter((x) => x.id !== toDelete.id));
      toast.success("Image removed");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setToDelete(null);
    }
  };

  const uploading = progress !== null;

  return (
    <div className="space-y-6" data-testid="media-library">
      <AdminPageHeader
        crumbs={CRUMBS}
        eyebrow="Assets"
        title="Media Library"
        description="Photos you upload here (or from any page editor) are stored safely in the cloud and can be reused on every landing page."
        actions={
          <>
            <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="media-file-input" />
            <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="h-11 rounded-none px-6 text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid="media-upload">
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              Upload images
            </Button>
          </>
        }
      />

      {uploading && (
        <div className="space-y-1.5 border border-border bg-card p-4" data-testid="media-progress">
          <div className="flex items-center justify-between text-xs">
            <span className="truncate text-muted-foreground">{progress < 100 ? `Uploading ${fileName}…` : "Finishing up…"}</span>
            <span className="tabular-nums">{progress}%</span>
          </div>
          <Progress value={progress} className="h-1.5 rounded-none bg-secondary [&>div]:bg-primary" />
        </div>
      )}

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFiles(e.dataTransfer?.files);
        }}
        className="border border-dashed border-border bg-card/40 p-4 text-center text-xs text-muted-foreground"
        data-testid="media-dropzone"
      >
        Drag and drop images anywhere in this box to upload (JPG, PNG, WEBP, GIF or AVIF, up to 15 MB each).
      </div>

      {items === null ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {["a", "b", "c", "d"].map((k) => (
            <Skeleton key={k} className="h-56" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center border border-border bg-card px-6 py-20 text-center" data-testid="media-empty">
          <Images className="h-8 w-8 text-primary" />
          <p className="display mt-4 text-4xl uppercase">No images yet</p>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">Upload studio photos, artist work or promo graphics to use on your landing pages.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-5" data-testid="media-grid">
          {items.map((item) => (
            <MediaCard key={item.id} item={item} onDelete={setToDelete} />
          ))}
        </div>
      )}

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-none border-border bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="display text-3xl uppercase tracking-wide">Remove this image?</AlertDialogTitle>
            <AlertDialogDescription>“{toDelete?.original_name}” will disappear from the library. Landing pages that still use it will show a broken image until you pick a new one.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="media-delete-confirm">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MediaLibrary;
